import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Warehouse, Plus, Scan, Package, CheckCircle, Clock, AlertTriangle,
  RefreshCw, Loader2, Search, ChevronRight, ClipboardCheck, X,
  Camera, FileText, DollarSign, MapPin, User, PackageCheck, Trash2,
  CheckCircle2, XCircle, Edit2, Save, Ban, Barcode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status configuration
const statusConfig = {
  draft: { label: 'Draft', color: 'text-zinc-400', bg: 'bg-zinc-500/10', icon: FileText },
  in_progress: { label: 'In Progress', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Clock },
  completed: { label: 'Completed', color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle }
};

const ReturnInwardingPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  const [activeTab, setActiveTab] = useState('manifests');
  
  // Manifests state
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Create manifest dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [awbInput, setAwbInput] = useState('');
  const [scannedReturns, setScannedReturns] = useState([]);
  const [warehouseLocation, setWarehouseLocation] = useState('');
  const [manifestNotes, setManifestNotes] = useState('');
  const [creating, setCreating] = useState(false);
  
  // QC Dialog
  const [qcDialogOpen, setQcDialogOpen] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [qcData, setQcData] = useState({});
  const [saving, setSaving] = useState(false);
  // Rack autocomplete — racks loaded once per QC session so the
  // <datalist> in the Rack# field offers matches as the operator
  // types. The full company-scoped list (single GET) keeps things
  // simple — racks are typically <500 rows per warehouse.
  const [racks, setRacks] = useState([]);
  
  // Missing inventory tab
  const [missingClaims, setMissingClaims] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);
  
  // Warehouses
  const [warehouses, setWarehouses] = useState([]);
  
  const fetchManifests = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/v1/returns/inwarding/manifests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManifests(response.data.manifests || []);
    } catch (error) {
      console.error('Error fetching manifests:', error);
      toast.error('Failed to load manifests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  
  const fetchMissingClaims = useCallback(async () => {
    setMissingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/v1/returns/missing-inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMissingClaims(response.data.claims || []);
    } catch (error) {
      console.error('Error fetching missing claims:', error);
    } finally {
      setMissingLoading(false);
    }
  }, []);
  
  const fetchWarehouses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/warehouses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWarehouses(response.data.warehouses || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchManifests();
    fetchWarehouses();
  }, [fetchManifests, fetchWarehouses]);
  
  useEffect(() => {
    if (activeTab === 'missing') {
      fetchMissingClaims();
    }
  }, [activeTab, fetchMissingClaims]);
  
  // Scan AWB to add to manifest
  const handleScanAwb = async () => {
    if (!awbInput.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      // Search for return by AWB
      const response = await axios.get(
        `${API_BASE_URL}/v1/returns?search=${encodeURIComponent(awbInput.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const returns = response.data.returns || [];
      const foundReturn = returns.find(r => 
        r.pickup_awb?.toLowerCase() === awbInput.trim().toLowerCase() ||
        r.return_number?.toLowerCase() === awbInput.trim().toLowerCase()
      );
      
      if (!foundReturn) {
        toast.error('Return not found with this AWB/Return number');
      } else if (scannedReturns.find(r => r.id === foundReturn.id)) {
        toast.error('This return is already added');
      } else if (foundReturn.status !== 'delivered') {
        // Auto-promote: warehouse has the parcel in hand but the
        // courier hasn't pushed the rto_delivered scan yet (very
        // common — couriers update late evening). Trust the
        // operator's physical scan, advance the return to delivered,
        // and add a timeline entry so it's auditable.
        try {
          await axios.post(
            `${API_BASE_URL}/v1/returns/${foundReturn.id}/update-status`,
            null,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: {
                status: 'delivered',
                notes: 'Operator-confirmed delivery via inwarding scan',
              },
            }
          );
          // Optimistically reflect the new status locally so the
          // scanned-list rendering doesn't block on a refetch.
          const promoted = { ...foundReturn, status: 'delivered' };
          setScannedReturns(prev => [...prev, promoted]);
          toast.success(
            `Promoted ${foundReturn.return_number} (${foundReturn.status} → delivered) and added to manifest`,
          );
        } catch (e) {
          toast.error(
            e.response?.data?.detail
            || `Could not auto-promote ${foundReturn.return_number} from ${foundReturn.status}.`,
          );
        }
      } else {
        setScannedReturns(prev => [...prev, foundReturn]);
        toast.success(`Added ${foundReturn.return_number}`);
      }
    } catch (error) {
      toast.error('Failed to search for return');
    }
    
    setAwbInput('');
  };
  
  const handleRemoveScanned = (returnId) => {
    setScannedReturns(prev => prev.filter(r => r.id !== returnId));
  };
  
  const handleCreateManifest = async () => {
    if (scannedReturns.length === 0) {
      toast.error('Add at least one return to create manifest');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/inwarding/manifests`, {
        return_ids: scannedReturns.map(r => r.id),
        warehouse_location: warehouseLocation || null,
        notes: manifestNotes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Manifest created successfully');
      setCreateDialogOpen(false);
      setScannedReturns([]);
      setAwbInput('');
      setWarehouseLocation('');
      setManifestNotes('');
      fetchManifests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create manifest');
    } finally {
      setCreating(false);
    }
  };
  
  const openQcDialog = (manifest) => {
    setSelectedManifest(manifest);
    // Initialize QC data from manifest
    const initialQcData = {};
    manifest.items?.forEach(item => {
      initialQcData[item.return_id] = {};
      item.line_items?.forEach(li => {
        initialQcData[item.return_id][li.sku] = {
          good_qty: li.good_qty || 0,
          damaged_qty: li.damaged_qty || 0,
          missing_qty: li.missing_qty || 0,
          rack_number: li.rack_number || '',
          notes: li.qc_notes || ''
        };
      });
    });
    setQcData(initialQcData);
    setQcDialogOpen(true);
    // Lazy-load racks once per dialog open so the autocomplete
    // datalist has fresh entries (a new rack created via Inventory
    // Settings while the page was open will appear next time).
    fetchRacks();
  };

  const fetchRacks = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE_URL}/enterprise/warehouse/racks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRacks(r.data?.racks || []);
    } catch (e) {
      // Non-fatal — autocomplete falls back to free-text. The backend
      // still validates, so a typo won't silently corrupt inventory.
      // eslint-disable-next-line no-console
      console.error('[Inwarding] failed to load racks for autocomplete', e);
    }
  };

  // Deep-link: ?create_return_id=<id> pre-fills the create-manifest dialog
  // with that return; ?qc_manifest_id=<id> auto-opens the QC dialog.
  // Both come from shortcut buttons on the Returns page.
  useEffect(() => {
    const createReturnId = searchParams.get('create_return_id');
    if (!createReturnId || deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/v1/returns/${createReturnId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const ret = response.data.return || response.data;
        if (!ret?.id) {
          toast.error('Return not found');
        } else if (ret.status === 'qc_pending') {
          toast.info(`${ret.return_number} is already in a manifest — open it below to process QC`);
        } else if (ret.status === 'delivered') {
          setScannedReturns([ret]);
          setCreateDialogOpen(true);
          toast.success(`${ret.return_number} added — complete the manifest details`);
        } else if (['pickup_assigned', 'picked_up', 'in_transit'].includes(ret.status)) {
          // Auto-promote: parcel is physically in hand but the courier
          // hasn't pushed the delivered scan yet — same behaviour as the
          // AWB scan flow. Advance to delivered with an audit note.
          try {
            const token2 = localStorage.getItem('token');
            await axios.post(
              `${API_BASE_URL}/v1/returns/${ret.id}/update-status`,
              null,
              {
                headers: { Authorization: `Bearer ${token2}` },
                params: {
                  status: 'delivered',
                  notes: 'Operator-confirmed delivery via Returns page manifest shortcut',
                },
              }
            );
            setScannedReturns([{ ...ret, status: 'delivered' }]);
            setCreateDialogOpen(true);
            toast.success(`Promoted ${ret.return_number} (${ret.status} → delivered) — complete the manifest details`);
          } catch (e2) {
            toast.error(e2.response?.data?.detail || `Could not auto-promote ${ret.return_number} from ${ret.status}`);
          }
        } else {
          toast.error(`${ret.return_number} is in "${ret.status}" status — it cannot be added to a manifest yet`);
        }
      } catch (e) {
        toast.error('Failed to load return for manifest');
      }
      setSearchParams({}, { replace: true });
    })();
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const qcManifestId = searchParams.get('qc_manifest_id');
    if (!qcManifestId || deepLinkHandled.current || manifests.length === 0) return;
    const manifest = manifests.find(m => m.id === qcManifestId);
    if (manifest) {
      deepLinkHandled.current = true;
      openQcDialog(manifest);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, manifests]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const updateQcField = (returnId, sku, field, value, lineItem = null) => {
    // For Good/Damaged/Missing quantity fields, enforce a hard cap so
    // ops can NEVER inward more than the customer actually returned.
    // The cap is `expected_qty - (sum of the other two buckets)`.
    // Without this guard the operator could mark "5 Good" on a return
    // of 2 items and silently break SSOT inventory.
    if (
      lineItem
      && ['good_qty', 'damaged_qty', 'missing_qty'].includes(field)
    ) {
      const expected = Number(lineItem.expected_qty ?? lineItem.quantity ?? 0);
      // Unknown/zero expected (legacy manifests where expected_qty was
      // never stamped) must NOT clamp operator input to 0 — that
      // silently completed manifests with zero quantities.
      if (expected > 0) {
        const current = qcData[returnId]?.[sku] || {};
        const buckets = { good_qty: 0, damaged_qty: 0, missing_qty: 0 };
        Object.assign(buckets, {
          good_qty: Number(current.good_qty || 0),
          damaged_qty: Number(current.damaged_qty || 0),
          missing_qty: Number(current.missing_qty || 0),
        });
        const otherSum = Object.entries(buckets)
          .filter(([k]) => k !== field)
          .reduce((s, [, v]) => s + v, 0);
        const remaining = Math.max(0, expected - otherSum);
        const n = Math.max(0, Math.min(remaining, Number(value || 0)));
        value = n;
      } else {
        value = Math.max(0, Number(value || 0));
      }
    }
    setQcData(prev => ({
      ...prev,
      [returnId]: {
        ...prev[returnId],
        [sku]: {
          ...prev[returnId]?.[sku],
          [field]: value
        }
      }
    }));
  };
  
  const handleSaveQc = async ({ silent = false } = {}) => {
    if (!selectedManifest) return;

    // Guard: block submit if any rack input contains a value that
    // doesn't match a loaded rack. Empty is allowed (rack optional),
    // but a typo is not — preventing silent inventory misplacement.
    const validRackSet = new Set(
      racks.map(r => (r.rack_number || '').toLowerCase())
    );
    for (const item of selectedManifest.items || []) {
      for (const li of item.line_items || []) {
        const rv = (qcData[item.return_id]?.[li.sku]?.rack_number || '').trim();
        if (rv && !validRackSet.has(rv.toLowerCase())) {
          const msg = `"${rv}" is not a valid rack — pick from the dropdown for SKU ${li.sku}.`;
          if (!silent) toast.error(msg);
          throw new Error(msg);
        }
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');

      // The backend QC endpoint is per-return:
      //   POST /v1/returns/inwarding/manifests/{id}/qc
      //   { return_id, items: [{ sku, good_qty, damaged_qty,
      //                          missing_qty, rack_number, photos, notes }] }
      // Loop the manifest's returns and post one at a time. If any
      // call fails we re-throw so the caller (`handleCompleteManifest`)
      // can abort BEFORE firing the complete endpoint — otherwise
      // complete fires with stale zero qtys and no inventory moves.
      for (const item of selectedManifest.items || []) {
        const qcItems = (item.line_items || []).map(li => ({
          sku: li.sku,
          good_qty: parseInt(qcData[item.return_id]?.[li.sku]?.good_qty) || 0,
          damaged_qty: parseInt(qcData[item.return_id]?.[li.sku]?.damaged_qty) || 0,
          missing_qty: parseInt(qcData[item.return_id]?.[li.sku]?.missing_qty) || 0,
          rack_number: qcData[item.return_id]?.[li.sku]?.rack_number || null,
          notes: qcData[item.return_id]?.[li.sku]?.notes || null,
        }));
        await axios.post(
          `${API_BASE_URL}/v1/returns/inwarding/manifests/${selectedManifest.id}/qc`,
          { return_id: item.return_id, items: qcItems },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      if (!silent) {
        toast.success('QC data saved');
        fetchManifests();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error?.message || 'Failed to save QC data';
      if (!silent) toast.error(detail);
      // Re-throw so callers can know save failed and stop their flow.
      throw error;
    } finally {
      setSaving(false);
    }
  };
  
  const handleCompleteManifest = async () => {
    if (!selectedManifest) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');

      // Save QC first. Pass silent=true so saveQc doesn't show its
      // own "QC data saved" toast or refetch mid-flow. If save fails,
      // saveQc re-throws → we surface ONE error toast and DO NOT
      // fire complete (which would otherwise persist zero qtys and
      // skip the inventory bump entirely).
      try {
        await handleSaveQc({ silent: true });
      } catch (saveErr) {
        const detail = saveErr.response?.data?.detail || saveErr?.message || 'Failed to save QC data';
        toast.error(`QC save failed — complete aborted. ${detail}`);
        return;
      }

      // Then complete the manifest.
      await axios.post(`${API_BASE_URL}/v1/returns/inwarding/manifests/${selectedManifest.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Manifest completed - inventory updated');
      setQcDialogOpen(false);
      fetchManifests();
      fetchMissingClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete manifest');
    } finally {
      setSaving(false);
    }
  };
  
  const handleResolveClaim = async (claimId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/v1/returns/missing-inventory/${claimId}/resolve?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Claim marked as ${status}`);
      fetchMissingClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve claim');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/5">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-white flex items-center gap-2">
                <Warehouse className="w-5 h-5 text-indigo-400" />
                Return Inwarding
              </h1>
              <p className="text-sm text-zinc-500 mt-0.5">
                QC, rack assignment, and inventory management for returned items
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => fetchManifests(true)}
                variant="outline"
                size="sm"
                className="border-white/10 bg-white/5 hover:bg-white/10"
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
                size="sm"
                data-testid="create-manifest-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Manifest
              </Button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="px-4 pb-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger value="manifests" className="data-[state=active]:bg-indigo-600">
                <ClipboardCheck className="w-4 h-4 mr-1" />
                Manifests
              </TabsTrigger>
              <TabsTrigger value="missing" className="data-[state=active]:bg-indigo-600">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Missing Inventory
                {missingClaims.length > 0 && (
                  <Badge className="ml-2 bg-red-500/20 text-red-400 text-xs">
                    {missingClaims.filter(c => c.status === 'pending').length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        {activeTab === 'manifests' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
              </div>
            ) : manifests.length === 0 ? (
              <div className="text-center py-12 bg-white/[0.02] rounded-xl border border-white/5">
                <Warehouse className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">No inwarding manifests</p>
                <p className="text-sm text-zinc-600 mt-1">Create a manifest to start QC on returned items</p>
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create Manifest
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {manifests.map((manifest, index) => {
                  const status = statusConfig[manifest.status] || statusConfig.draft;
                  const StatusIcon = status.icon;
                  // Each line_item carries `expected_qty` (the qty the
                  // customer returned). The old code summed `li.quantity`
                  // — a field that was never set on the manifest line
                  // items — so Total Items always showed 0. Fall back
                  // to `quantity` for any historical rows that did set
                  // it, but `expected_qty` is the canonical field.
                  const totalItems = manifest.items?.reduce((sum, item) =>
                    sum + (item.line_items?.reduce(
                      (s, li) => s + Number(li.expected_qty ?? li.quantity ?? 0),
                      0,
                    ) || 0), 0) || 0;
                  
                  return (
                    <motion.div
                      key={manifest.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white/[0.02] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-all"
                      data-testid={`manifest-card-${manifest.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-medium text-white">
                              {manifest.manifest_number}
                            </span>
                            <Badge className={`${status.bg} ${status.color} text-xs`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                          </div>
                          <p className="text-xs text-zinc-500">
                            Created: {formatDate(manifest.created_at)}
                          </p>
                        </div>
                        {manifest.status !== 'completed' && (
                          <Button
                            onClick={() => openQcDialog(manifest)}
                            className="bg-indigo-600 hover:bg-indigo-700"
                            size="sm"
                          >
                            <ClipboardCheck className="w-4 h-4 mr-1" />
                            {manifest.status === 'draft' ? 'Start QC' : 'Continue QC'}
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="bg-white/[0.02] rounded-lg p-3">
                          <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
                            <Package className="w-3.5 h-3.5" />
                            <span className="text-xs">Returns</span>
                          </div>
                          <p className="text-white font-medium">{manifest.items?.length || 0}</p>
                        </div>
                        <div className="bg-white/[0.02] rounded-lg p-3">
                          <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
                            <Barcode className="w-3.5 h-3.5" />
                            <span className="text-xs">Total Items</span>
                          </div>
                          <p className="text-white font-medium">{totalItems}</p>
                        </div>
                        {manifest.warehouse_location && (
                          <div className="bg-white/[0.02] rounded-lg p-3">
                            <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
                              <MapPin className="w-3.5 h-3.5" />
                              <span className="text-xs">Warehouse</span>
                            </div>
                            <p className="text-white font-medium truncate">{manifest.warehouse_location}</p>
                          </div>
                        )}
                      </div>
                      
                      {manifest.status === 'completed' && (
                        <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-3 gap-4 text-sm">
                          <div className="text-center">
                            {/* Backend writes total_good / total_damaged /
                                total_missing as flat fields on the manifest
                                doc (see complete_inwarding_manifest in
                                returns_module.py). The old code read from a
                                non-existent `summary.*` path so every
                                completed manifest displayed 0/0/0. Falling
                                back to summary.* covers any historical row
                                that wrote it that way. */}
                            <p className="text-green-400 font-medium">
                              {manifest.total_good ?? manifest.summary?.total_good ?? 0}
                            </p>
                            <p className="text-xs text-zinc-500">Good</p>
                          </div>
                          <div className="text-center">
                            <p className="text-red-400 font-medium">
                              {manifest.total_damaged ?? manifest.summary?.total_damaged ?? 0}
                            </p>
                            <p className="text-xs text-zinc-500">Damaged</p>
                          </div>
                          <div className="text-center">
                            <p className="text-amber-400 font-medium">
                              {manifest.total_missing ?? manifest.summary?.total_missing ?? 0}
                            </p>
                            <p className="text-xs text-zinc-500">Missing</p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}
        
        {activeTab === 'missing' && (
          <>
            {missingLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              </div>
            ) : missingClaims.length === 0 ? (
              <div className="text-center py-12 bg-white/[0.02] rounded-xl border border-white/5">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-zinc-400">No missing inventory claims</p>
                <p className="text-sm text-zinc-600 mt-1">All returned items have been accounted for</p>
              </div>
            ) : (
              <div className="space-y-4">
                {missingClaims.map((claim, index) => (
                  <motion.div
                    key={claim.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/[0.02] border border-white/5 rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-white">{claim.product_name}</span>
                          <Badge className={
                            claim.status === 'pending' ? 'bg-amber-500/10 text-amber-400' :
                            claim.status === 'received' ? 'bg-green-500/10 text-green-400' :
                            claim.status === 'written_off' ? 'bg-red-500/10 text-red-400' :
                            'bg-blue-500/10 text-blue-400'
                          }>
                            {claim.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-zinc-500 font-mono">SKU: {claim.sku}</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          Return: {claim.return_number} | Qty: {claim.quantity}
                        </p>
                      </div>
                      {claim.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleResolveClaim(claim.id, 'received')}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Received
                          </Button>
                          <Button
                            onClick={() => handleResolveClaim(claim.id, 'written_off')}
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Write Off
                          </Button>
                          <Button
                            onClick={() => handleResolveClaim(claim.id, 'claim_received')}
                            size="sm"
                            variant="outline"
                            className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          >
                            <DollarSign className="w-3 h-3 mr-1" />
                            Claimed
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500">
                      Reported: {formatDate(claim.created_at)}
                      {claim.resolved_at && ` | Resolved: ${formatDate(claim.resolved_at)}`}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Create Manifest Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-indigo-400" />
              Create Inwarding Manifest
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* AWB Scanner */}
            <div>
              <Label>Scan AWB / Return Number</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Scan className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    value={awbInput}
                    onChange={(e) => setAwbInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleScanAwb()}
                    className="bg-white/5 border-white/10 pl-10"
                    placeholder="Scan or enter AWB..."
                    autoFocus
                  />
                </div>
                <Button onClick={handleScanAwb} className="bg-indigo-600 hover:bg-indigo-700">
                  Add
                </Button>
              </div>
            </div>
            
            {/* Scanned Returns */}
            <div>
              <Label className="mb-2 block">Returns to Inward ({scannedReturns.length})</Label>
              {scannedReturns.length === 0 ? (
                <div className="text-center py-6 bg-white/[0.02] rounded-lg border border-dashed border-white/10">
                  <Package className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">Scan AWB numbers to add returns</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {scannedReturns.map(ret => (
                    <div key={ret.id} className="flex items-center justify-between p-2 bg-white/[0.02] rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-white">{ret.return_number}</p>
                        <p className="text-xs text-zinc-500">AWB: {ret.pickup_awb || 'N/A'}</p>
                      </div>
                      <Button
                        onClick={() => handleRemoveScanned(ret.id)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Warehouse */}
            <div>
              <Label>Warehouse Location</Label>
              <Select value={warehouseLocation} onValueChange={setWarehouseLocation}>
                <SelectTrigger className="bg-white/5 border-white/10 mt-1" data-testid="inwarding-warehouse-trigger">
                  <SelectValue placeholder="Select warehouse..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {warehouses.length > 0 ? (
                    warehouses.map(w => (
                      <SelectItem
                        key={w.id}
                        value={w.name}
                        data-testid={`inwarding-warehouse-option-${w.id}`}
                      >
                        {w.name}{w.is_primary ? ' (Primary)' : ''}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-wt-text-muted" data-testid="inwarding-no-warehouses">
                      No warehouses configured. Add one in Settings → Warehouses.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={manifestNotes}
                onChange={(e) => setManifestNotes(e.target.value)}
                className="bg-white/5 border-white/10 mt-1"
                placeholder="Any notes for this manifest..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateManifest} 
              disabled={creating || scannedReturns.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Manifest
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* QC Dialog */}
      <Dialog open={qcDialogOpen} onOpenChange={setQcDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-indigo-400" />
              Quality Check - {selectedManifest?.manifest_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Shared <datalist> for all Rack# inputs in this dialog.
                Browsers natively render dropdown suggestions as the
                operator types — zero JS overhead. */}
            <datalist id="inwarding-rack-options">
              {racks.map(r => (
                <option
                  key={r.id || r.rack_number}
                  value={r.rack_number}
                  data-testid={`rack-option-${r.rack_number}`}
                >
                  {r.location_name ? `${r.rack_number} — ${r.location_name}` : r.rack_number}
                </option>
              ))}
            </datalist>
            {selectedManifest?.items?.map((item, itemIndex) => (
              <div key={item.return_id} className="bg-white/[0.02] rounded-xl p-4 border border-white/5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-medium text-white">{item.return_number}</p>
                    <p className="text-xs text-zinc-500">Return ID: {item.return_id}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {item.line_items?.map((li, liIndex) => {
                    // Canonical expected quantity (returned by the
                    // create-manifest endpoint as `expected_qty`).
                    // Fallback to legacy `quantity` for historical rows.
                    const expectedQty = Number(li.expected_qty ?? li.quantity ?? 0);
                    const buckets = qcData[item.return_id]?.[li.sku] || {};
                    const goodN = Number(buckets.good_qty || 0);
                    const damagedN = Number(buckets.damaged_qty || 0);
                    const missingN = Number(buckets.missing_qty || 0);
                    const totalEntered = goodN + damagedN + missingN;
                    const remaining = Math.max(0, expectedQty - totalEntered);
                    // Strict rack validation — the value must match one
                    // of the racks loaded for this location. Empty is
                    // allowed (rack is optional), but a typo is not.
                    const rackVal = (buckets.rack_number || '').trim();
                    const rackValid = !rackVal || racks.some(
                      r => (r.rack_number || '').toLowerCase() === rackVal.toLowerCase()
                    );
                    return (
                    <div key={li.sku} className="bg-white/[0.02] rounded-lg p-3">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm font-medium text-white">{li.product_name}</p>
                          <p className="text-xs text-zinc-500 font-mono">
                            SKU: {li.sku} | Expected Qty: <span className="text-white">{expectedQty}</span>
                            {totalEntered > 0 && (
                              <span className={`ml-2 ${remaining === 0 ? 'text-green-400' : 'text-amber-400'}`}>
                                • Entered: {totalEntered}/{expectedQty}
                                {remaining > 0 && ` (${remaining} pending)`}
                              </span>
                            )}
                          </p>
                          {/* Customer's photo evidence (uploaded via portal) */}
                          {(li.customer_photos || []).length > 0 && (
                            <div className="flex items-center gap-2 mt-2" data-testid={`qc-customer-photos-${li.sku}`}>
                              <span className="text-[10px] uppercase tracking-wide text-zinc-500">Customer photos:</span>
                              {li.customer_photos.map((url, pi) => (
                                <a key={pi} href={url} target="_blank" rel="noreferrer">
                                  <img
                                    src={url}
                                    alt="customer evidence"
                                    className="w-12 h-12 object-cover rounded-lg border border-amber-500/30 hover:border-amber-400 transition-colors"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-5 gap-3">
                        <div>
                          <Label className="text-xs text-green-400">Good</Label>
                          <Input
                            type="number"
                            value={buckets.good_qty || 0}
                            onChange={(e) => updateQcField(item.return_id, li.sku, 'good_qty', e.target.value, li)}
                            className="bg-green-500/10 border-green-500/30 mt-1 text-green-400"
                            min={0}
                            max={expectedQty - damagedN - missingN}
                            data-testid={`qc-good-${li.sku}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-red-400">Damaged</Label>
                          <Input
                            type="number"
                            value={buckets.damaged_qty || 0}
                            onChange={(e) => updateQcField(item.return_id, li.sku, 'damaged_qty', e.target.value, li)}
                            className="bg-red-500/10 border-red-500/30 mt-1 text-red-400"
                            min={0}
                            max={expectedQty - goodN - missingN}
                            data-testid={`qc-damaged-${li.sku}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-amber-400">Missing</Label>
                          <Input
                            type="number"
                            value={buckets.missing_qty || 0}
                            onChange={(e) => updateQcField(item.return_id, li.sku, 'missing_qty', e.target.value, li)}
                            className="bg-amber-500/10 border-amber-500/30 mt-1 text-amber-400"
                            min={0}
                            max={expectedQty - goodN - damagedN}
                            data-testid={`qc-missing-${li.sku}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Rack #</Label>
                          {/* Typeahead, NOT a long popup: <datalist> only
                              surfaces matches as the operator types. The
                              border turns red when the entered value
                              doesn't match any loaded rack — visual cue
                              before submit. `save` is guarded below so
                              a typo can't be persisted. */}
                          <Input
                            list="inwarding-rack-options"
                            value={buckets.rack_number || ''}
                            onChange={(e) => updateQcField(item.return_id, li.sku, 'rack_number', e.target.value)}
                            className={`mt-1 ${
                              rackVal && !rackValid
                                ? 'bg-red-500/10 border-red-500/60 text-red-400'
                                : 'bg-white/5 border-white/10'
                            }`}
                            placeholder="Type to search…"
                            autoComplete="off"
                            data-testid={`inwarding-rack-input-${li.sku}`}
                          />
                          {rackVal && !rackValid && (
                            <p className="text-[10px] text-red-400 mt-1">
                              Not a valid rack
                            </p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Notes</Label>
                          <Input
                            value={buckets.notes || ''}
                            onChange={(e) => updateQcField(item.return_id, li.sku, 'notes', e.target.value)}
                            className="bg-white/5 border-white/10 mt-1"
                            placeholder="QC notes..."
                          />
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setQcDialogOpen(false)} className="border-white/10">
              Cancel
            </Button>
            <Button onClick={handleSaveQc} disabled={saving} variant="outline" className="border-indigo-500/30 text-indigo-400">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Progress
            </Button>
            <Button onClick={handleCompleteManifest} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Complete & Update Inventory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReturnInwardingPage;
